import { type ChangeEvent, type FC, Fragment, memo, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import CloseIcon from 'assets/svg/app/close.svg'
import ResetIcon from 'assets/svg/app/reset.svg'

import OrderModeLeft from 'assets/svg/futures/order-mode-left.svg'
import OrderModeRight from 'assets/svg/futures/order-mode-right.svg'

import UnderlineTabs from 'components/Button/UnderlineTabs'
import Switch from 'components/Switch'
import { TabPanel } from 'components/Tab'
import { Body } from 'components/Text'
import {
	FlexDivCol,
	FlexDivColCentered,
	FlexDivRow,
	FlexDivRowCentered,
} from 'components/layout/flex'
import useWindowSize from 'hooks/useWindowSize'
import { useRouter } from 'next/router'
import { selectOneClickTradingSelected, selectPerpsProvider } from 'state/futures/common/selectors'
import { resetPreferences, setMarketModules, setPreferences } from 'state/futures/reducer'
import {
	selectCustomLayout,
	selectOrderMode,
	selectShowFunding,
	selectShowMarketModules,
	selectShowOrderLines,
	selectShowPositionLines,
	selectShowRealizedPnL,
	selectShowTestnets,
} from 'state/futures/selectors'
import { selectCondOrdersEnabled } from 'state/futures/snxPerpsV3/selectors'
import { type ModuleType, TradingModes } from 'state/futures/types'
import { useAppDispatch, useAppSelector } from 'state/hooks'
import { providerIsCrossMargin } from 'utils/futures'

import Button from 'components/Button'
import { DEFAULT_LAYOUTS_LEFT, DEFAULT_LAYOUTS_RIGHT } from 'state/constants'
import { resetButtonCSS } from 'styles/common'

export enum PreferenceTab {
	STYLE = 'style',
	FEATURES = 'features',
}

type PreferencePanelProps = {
	onDismiss: () => void
}

const PreferencesPanel: FC<PreferencePanelProps> = memo(({ onDismiss }) => {
	const { t } = useTranslation()
	const router = useRouter()
	const dispatch = useAppDispatch()
	const perpsProvider = useAppSelector(selectPerpsProvider)
	const orderMode = useAppSelector(selectOrderMode)
	const showMarketModules = useAppSelector(selectShowMarketModules)
	const showPositionLines = useAppSelector(selectShowPositionLines)
	const customLayout = useAppSelector(selectCustomLayout)
	const showOrderLines = useAppSelector(selectShowOrderLines)
	const showRealizedPnL = useAppSelector(selectShowRealizedPnL)
	const showTestnets = useAppSelector(selectShowTestnets)
	const showFunding = useAppSelector(selectShowFunding)
	const isOneClickSelected = useAppSelector(selectOneClickTradingSelected)
	const condOrdersEnabled = useAppSelector(selectCondOrdersEnabled)

	const [preferenceActiveTab, setPreferenceActiveTab] = useState<PreferenceTab>(PreferenceTab.STYLE)
	const { deviceType } = useWindowSize()
	const isMobile = useMemo(() => deviceType === 'mobile', [deviceType])

	const isMarketsPage = useMemo(() => router.pathname.includes('market'), [router.pathname])

	const handleReset = useCallback(() => dispatch(resetPreferences()), [dispatch])

	const toggleOneClickTrading = useCallback(
		(_: ChangeEvent<HTMLInputElement>) =>
			dispatch(
				setPreferences({
					tradingMode: isOneClickSelected ? TradingModes.DEFAULT : TradingModes.ONE_CLICK,
				})
			),
		[dispatch, isOneClickSelected]
	)

	const toggleShowModule = useCallback(
		(module: ModuleType, e: ChangeEvent<HTMLInputElement>) =>
			dispatch(setMarketModules({ module, value: e.target.checked })),
		[dispatch]
	)

	const togglePositionLines = useCallback(
		(e: ChangeEvent<HTMLInputElement>) =>
			dispatch(setPreferences({ showPositionLines: e.target.checked })),
		[dispatch]
	)

	const toggleOrderLines = useCallback(
		(e: ChangeEvent<HTMLInputElement>) =>
			dispatch(setPreferences({ showOrderLines: e.target.checked })),
		[dispatch]
	)

	const toggleRealizedPnL = useCallback(
		(e: ChangeEvent<HTMLInputElement>) =>
			dispatch(setPreferences({ showRealizedPnL: e.target.checked })),
		[dispatch]
	)

	const toggleTestnets = useCallback(
		(e: ChangeEvent<HTMLInputElement>) =>
			dispatch(setPreferences({ showTestnets: e.target.checked })),
		[dispatch]
	)

	const toggleFunding = useCallback(
		(e: ChangeEvent<HTMLInputElement>) =>
			dispatch(setPreferences({ showFunding: e.target.checked })),
		[dispatch]
	)

	const setOrderMode = useCallback(
		(mode: 'left' | 'right') =>
			dispatch(
				setPreferences({
					orderMode: mode,
					layouts: mode === 'left' ? DEFAULT_LAYOUTS_LEFT : DEFAULT_LAYOUTS_RIGHT,
				})
			),
		[dispatch]
	)

	const toggleCustomLayout = useCallback(
		(e: ChangeEvent<HTMLInputElement>) => {
			dispatch(
				setPreferences({
					layouts: orderMode === 'left' ? DEFAULT_LAYOUTS_LEFT : DEFAULT_LAYOUTS_RIGHT,
				})
			)
			dispatch(setPreferences({ customLayout: e.target.checked }))
		},
		[dispatch, orderMode]
	)

	const preferenceTabs = useMemo(
		() => [
			{
				name: PreferenceTab.STYLE,
				label: t('common.preferences.style'),
				active: preferenceActiveTab === PreferenceTab.STYLE,
				onClick: (): void => {
					setPreferenceActiveTab(PreferenceTab.STYLE)
				},
				preferences: [
					{
						title: t('common.preferences.layout-style'),
						subOptions: [
							{
								title: t('common.preferences.customize-layout'),
								option: <Switch checked={customLayout} onChange={toggleCustomLayout} />,
							},
							{
								title: t('common.preferences.trade-history'),
								option: (
									<Switch
										checked={showMarketModules.history}
										onChange={(e) => toggleShowModule('history', e)}
									/>
								),
							},
							{
								title: t('common.preferences.favorites'),
								option: (
									<Switch
										checked={showMarketModules.favorites}
										onChange={(e) => toggleShowModule('favorites', e)}
									/>
								),
							},
							{
								title: t('common.preferences.user-info'),
								option: (
									<Switch
										checked={showMarketModules.userInfo}
										onChange={(e) => toggleShowModule('userInfo', e)}
									/>
								),
							},
							// {
							// 	title: t('common.preferences.market-details'),
							// 	option: (
							// 		<Switch
							// 			checked={showMarketModules.marketDetails}
							// 			onChange={(e) => toggleShowModule('marketDetails', e)}
							// 		/>
							// 	),
							// },
							{
								title: t('common.preferences.price-chart'),
								option: (
									<Switch
										checked={showMarketModules.priceChart}
										onChange={(e) => toggleShowModule('priceChart', e)}
									/>
								),
							},
							{
								title: t('common.preferences.account-details'),
								option: (
									<Switch
										checked={showMarketModules.accountDetails}
										onChange={(e) => toggleShowModule('accountDetails', e)}
									/>
								),
							},
						],
					},
					{
						title: t('common.preferences.chart.title'),
						subOptions: [
							{
								title: t('common.preferences.chart.positions'),
								option: <Switch checked={showPositionLines} onChange={togglePositionLines} />,
							},
							{
								title: t('common.preferences.chart.active-orders'),
								option: <Switch checked={showOrderLines} onChange={toggleOrderLines} />,
								hide: providerIsCrossMargin(perpsProvider) && !condOrdersEnabled,
							},
						],
					},
					{
						title: t('common.preferences.position-table.title'),
						subOptions: [
							{
								title: t('common.preferences.position-table.funding'),
								option: <Switch checked={showFunding} onChange={toggleFunding} />,
							},
							{
								title: t('common.preferences.position-table.rpnl'),
								option: <Switch checked={showRealizedPnL} onChange={toggleRealizedPnL} />,
							},
						],
					},
				],
			},
			{
				name: PreferenceTab.FEATURES,
				label: t('common.preferences.features'),
				active: preferenceActiveTab === PreferenceTab.FEATURES,
				onClick: (): void => {
					setPreferenceActiveTab(PreferenceTab.FEATURES)
				},
				preferences: [
					{
						title: t('common.preferences.one-click-trading.title'),
						subOptions: [
							{
								title: t('common.preferences.one-click-trading.sign-transactions'),
								option: <Switch checked={isOneClickSelected} onChange={toggleOneClickTrading} />,
							},
						],
					},
					{
						title: t('common.preferences.dev-tools.title'),
						subOptions: [
							{
								title: t('common.preferences.dev-tools.show-testnets'),
								option: <Switch checked={showTestnets} onChange={toggleTestnets} />,
							},
						],
					},
				],
			},
		],
		[
			preferenceActiveTab,
			t,
			showMarketModules,
			showPositionLines,
			customLayout,
			showOrderLines,
			showFunding,
			showRealizedPnL,
			showTestnets,
			isOneClickSelected,
			toggleShowModule,
			togglePositionLines,
			toggleCustomLayout,
			toggleOrderLines,
			toggleFunding,
			toggleRealizedPnL,
			toggleTestnets,
			toggleOneClickTrading,
			perpsProvider,
			condOrdersEnabled,
		]
	)

	return (
		(isMarketsPage || isMobile) && (
			<Container>
				<FlexDivCol rowGap="16px">
					<FixedTitleContainer>
						<DismissButton onClick={onDismiss}>
							<CloseIcon width={18} height={18} />
						</DismissButton>
						<Title> {t('common.preferences.title')}</Title>
					</FixedTitleContainer>
					<TabButtonsContainer>
						<UnderlineTabs options={preferenceTabs} />
					</TabButtonsContainer>
					{preferenceTabs.map(({ name, preferences }) => (
						<TabPanel key={name} name={name} activeTab={preferenceActiveTab} fullHeight>
							<TabPanelContainer>
								{name === PreferenceTab.STYLE && (
									<>
										<FlexDivCol rowGap="16px">
											<Title color="secondary">{t('common.preferences.order-mode.title')}</Title>
											<FlexDivRow columnGap="16px">
												<OrderModeLeftComponent
													active={orderMode === 'left'}
													onClick={() => setOrderMode('left')}
												/>
												<OrderModeRightComponent
													active={orderMode === 'right'}
													onClick={() => setOrderMode('right')}
												/>
											</FlexDivRow>
											<FlexDivRow columnGap="16px">
												<OrderModeContainer>
													<Body
														color={orderMode === 'left' ? 'primary' : 'secondary'}
														weight="bold"
													>
														{t('common.preferences.order-mode.left')}
													</Body>
												</OrderModeContainer>
												<OrderModeContainer>
													<Body
														color={orderMode === 'right' ? 'primary' : 'secondary'}
														weight="bold"
													>
														{t('common.preferences.order-mode.right')}
													</Body>
												</OrderModeContainer>
											</FlexDivRow>
										</FlexDivCol>
										<SectionSeparator />
									</>
								)}

								{preferences.map(({ title, subOptions }, index) => (
									<Fragment key={`group-${title}`}>
										{index !== 0 && <SectionSeparator />}
										<FlexDivCol rowGap="16px">
											<Title color="secondary">{title}</Title>
											<FlexDivCol rowGap="16px">
												{subOptions
													.filter(({ hide }) => !hide)
													.map(({ title, option }) => (
														<FlexDivRowCentered key={`suboption-${title}`}>
															<Body color="primary" weight="bold" size="large">
																{title}
															</Body>
															{option}
														</FlexDivRowCentered>
													))}
											</FlexDivCol>
										</FlexDivCol>
									</Fragment>
								))}
							</TabPanelContainer>
						</TabPanel>
					))}
				</FlexDivCol>
				<FlexDivCol rowGap="16px">
					<SectionSeparator />
					<Button>
						<ResetContainer justifyContent="center" columnGap="8px" onClick={handleReset}>
							<ResetIcon width={12} height={12} />
							<Body size="medium" weight="bold">
								{t('common.preferences.reset')}
							</Body>
						</ResetContainer>
					</Button>
				</FlexDivCol>
			</Container>
		)
	)
})

const ResetContainer = styled(FlexDivRowCentered)`
	cursor: pointer;
	svg {
		path {
			color: ${(props) => props.theme.colors.selectedTheme.newTheme.text.primary};
		}
	}
`

const DismissButton = styled.button`
	${resetButtonCSS};
	position: fixed;
	top: 15px;
	right: 15px;
	color: ${(props) => props.theme.colors.selectedTheme.gray};
	&:hover {
		color: ${(props) => props.theme.colors.selectedTheme.button.text.primary};
	}
`

const Title = styled(Body)`
	font-size: 18px;
	font-weight: 600;
`

const FixedTitleContainer = styled(FlexDivRow)`
	position: fixed;
	z-index: 10;
	top: 0px;
	left: 0px;
	width: 100%;
	height: 50px;
	padding: 15px;
	background: ${(props) => props.theme.colors.selectedTheme.newTheme.containers.cards.background};
	border-radius: 4px;
`

const SectionSeparator = styled.div`
	height: 1px;
	background-color: ${(props) => props.theme.colors.selectedTheme.newTheme.border.color};
`

const Container = styled.div`
	position: relative;
	display: flex;
	flex-direction: column;
	justify-content: space-between;
	width: 100%;
	height: 100%;
	overflow-y: scroll;
	border: none;
	row-gap: 16px;
	border-radius: 8px;
	padding: 15px;
	padding-top: 44px;
`

const TabPanelContainer = styled(FlexDivCol)`
	row-gap: 15px;
`

const TabButtonsContainer = styled.div`
	display: flex;
	min-height: 46px;
	gap: 15px;
	width: 100%;

	button {
		font-size: 13px;
	}

	> div > div {
		border-right: none;
		padding-left: 0px;
	}

	border-bottom: ${(props) => props.theme.colors.selectedTheme.border};
`

const OrderModeLeftComponent = styled(OrderModeLeft)<{ active?: boolean }>`
	cursor: pointer;
	> .border {
		visibility: ${(props) => (props.active ? 'visible' : 'hidden')};
	}
`

const OrderModeRightComponent = styled(OrderModeRight)<{ active?: boolean }>`
	cursor: pointer;
	> .border {
		visibility: ${(props) => (props.active ? 'visible' : 'hidden')};
	}
`

const OrderModeContainer = styled(FlexDivColCentered)`
	width: 50%;
`

export default PreferencesPanel
